/**
 * The Fresh AI texts lever, in numbers (user, 15/10/2026: "we still don't
 * see anything"): for each prompt group, how many of the drops its AI
 * texts would go to — taking turns vs leaning on the best — through the
 * engine's own `leanWeights` + rotation. The container's groups when one
 * has results; the example ranking's made-up groups when none has.
 * For Media Devoted | Created By Ciro Autiero
 */
import { leanDropCounts, leanWeights } from '@trafficpulse/shared';

export interface LeanGroup {
  id: string;
  name: string;
  /** The group's score on the ranking's formula; null = no results yet. */
  score: number | null;
}

export interface LeanRow extends LeanGroup {
  /** Drops it writes when the groups take turns. */
  turns: number;
  /** Drops it writes when the lean is on. */
  lean: number;
}

export interface LeanView {
  groups: LeanGroup[];
  /** True = the example's made-up groups (none of the container's has results). */
  example: boolean;
  /** How many drops the counts share out. */
  drops: number;
  /** True = today's real drops with fresh AI texts; false = a sample day of `drops`. */
  today: boolean;
}

/** A sample day when today has no drop with fresh AI texts. */
export const SAMPLE_DROPS = 20;

/** The container's own groups when one has a score; else the example's, when there is one. */
export function leanGroupsFor(own: LeanGroup[], example: LeanGroup[] | null): Pick<LeanView, 'groups' | 'example'> {
  if (own.some((g) => typeof g.score === 'number') || !example?.length) return { groups: own, example: false };
  return { groups: example, example: true };
}

export function leanRows(groups: LeanGroup[], drops: number): LeanRow[] {
  const ids = groups.map((g) => g.id);
  const scores = new Map(groups.map((g) => [g.id, g.score]));
  const turns = leanDropCounts(ids, null, drops);
  const lean = leanDropCounts(ids, leanWeights(ids, (id) => scores.get(id)), drops);
  return groups.map((g) => ({ ...g, turns: turns.get(g.id) ?? 0, lean: lean.get(g.id) ?? 0 }));
}
